// M08 二次函數與二次方程 - 主題 01-10 數據文件
// Quadratic Functions and Quadratic Equations - Topics 01-10
// 系統化設計，準備後續 Supabase 數據遷移

// M08-01: 二次不等式 (Quadratic Inequalities)
export const M08_TOPICS_01_TO_10 = [
  {
    id: 'M08_01',
    name: '二次不等式',
    description: '掌握二次不等式的求解方法與圖像分析',
    category: 'quadratic_inequalities',
    difficulty: 'intermediate'
  },
  {
    id: 'M08_02',
    name: '二次方程求根',
    description: '學習各種二次方程求根的方法與技巧',
    category: 'quadratic_roots',
    difficulty: 'basic'
  },
  {
    id: 'M08_03',
    name: '二次方程求解',
    description: '熟練運用不同方法解二次方程',
    category: 'quadratic_solving',
    difficulty: 'basic'
  },
  {
    id: 'M08_04',
    name: '公式法',
    description: '掌握二次方程公式法的應用',
    category: 'quadratic_formula',
    difficulty: 'intermediate'
  },
  {
    id: 'M08_05',
    name: '方程求解',
    description: '綜合運用各種方法求解方程',
    category: 'equation_solving',
    difficulty: 'intermediate'
  },
  {
    id: 'M08_06',
    name: '代數變換',
    description: '學習代數變換在二次方程中的應用',
    category: 'algebraic_transformation',
    difficulty: 'intermediate'
  },
  {
    id: 'M08_07',
    name: '回代求解',
    description: '掌握回代法在複雜方程中的運用',
    category: 'substitution_method',
    difficulty: 'advanced'
  },
  {
    id: 'M08_08',
    name: '因式分解',
    description: '熟練運用因式分解法解二次方程',
    category: 'factoring',
    difficulty: 'basic'
  },
  {
    id: 'M08_09',
    name: '判別式',
    description: '理解判別式在二次方程中的作用',
    category: 'discriminant',
    difficulty: 'intermediate'
  },
  {
    id: 'M08_10',
    name: '求根',
    description: '掌握二次方程根的性質與求法',
    category: 'root_finding',
    difficulty: 'intermediate'
  }
];

// M08 問題數據 - 主題 01-10
export const M08_PROBLEMS_01_TO_10 = [
  // M08-01: 二次不等式
  {
    id: 'M08_01_01',
    topicId: 'M08_01',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '解下列二次不等式：',
    parts: {
      a: {
        question: '求解不等式 $x^2 - 5x + 6 > 0$',
        solution: {
          steps: [
            '首先將二次式因式分解：$x^2 - 5x + 6 = (x-2)(x-3)$',
            '不等式變為 $(x-2)(x-3) > 0$',
            '使用數線測試法：當 $x < 2$ 或 $x > 3$ 時，兩因數同號且為正',
            '因此解為 $x \\in (-\\infty, 2) \\cup (3, +\\infty)$'
          ],
          answer: '$x \\in (-\\infty, 2) \\cup (3, +\\infty)$'
        },
        hint: '先因式分解，再使用數線測試法判斷符號'
      },
      b: {
        question: '求解不等式 $2x^2 + x - 3 \\leq 0$',
        solution: {
          steps: [
            '因式分解：$2x^2 + x - 3 = (2x+3)(x-1)$',
            '不等式變為 $(2x+3)(x-1) \\leq 0$',
            '找出零點：$x = -\\frac{3}{2}$ 和 $x = 1$',
            '使用數線測試：當 $-\\frac{3}{2} \\leq x \\leq 1$ 時，不等式成立'
          ],
          answer: '$x \\in [-\\frac{3}{2}, 1]$'
        },
        hint: '注意不等號包含等號，所以解集包含邊界點'
      },
      c: {
        question: '求解不等式 $x^2 - 4x + 5 > 0$',
        solution: {
          steps: [
            '計算判別式：$\\Delta = (-4)^2 - 4(1)(5) = 16 - 20 = -4 < 0$',
            '由於判別式小於零且二次項係數為正',
            '拋物線開口向上且不與 x 軸相交',
            '因此 $x^2 - 4x + 5 > 0$ 對所有實數 $x$ 都成立'
          ],
          answer: '$x \\in \\mathbb{R}$'
        },
        hint: '當判別式小於零時，要根據二次項係數判斷拋物線位置'
      }
    }
  },
  {
    id: 'M08_01_02',
    topicId: 'M08_01',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '已知二次函數 $f(x) = ax^2 + bx + c$，分析不等式：',
    parts: {
      a: {
        question: '若 $f(x) = x^2 - 6x + 8$，求 $f(x) < 0$ 的解集',
        solution: {
          steps: [
            '因式分解：$x^2 - 6x + 8 = (x-2)(x-4)$',
            '不等式 $(x-2)(x-4) < 0$ 表示兩因數異號',
            '當 $2 < x < 4$ 時，$(x-2) > 0$ 且 $(x-4) < 0$',
            '因此解集為 $(2, 4)$'
          ],
          answer: '$x \\in (2, 4)$'
        },
        hint: '找出使二次函數值為負的 x 值範圍'
      },
      b: {
        question: '求 $f(x) \\geq 0$ 的解集',
        solution: {
          steps: [
            '由上題知 $f(x) = (x-2)(x-4)$',
            '$f(x) \\geq 0$ 表示 $(x-2)(x-4) \\geq 0$',
            '當 $x \\leq 2$ 或 $x \\geq 4$ 時，兩因數同號',
            '包含等號，所以解集為 $(-\\infty, 2] \\cup [4, +\\infty)$'
          ],
          answer: '$x \\in (-\\infty, 2] \\cup [4, +\\infty)$'
        },
        hint: '注意包含等號的情況，零點也要包括在解集中'
      },
      c: {
        question: '若要使 $f(x) + k > 0$ 對所有實數 $x$ 都成立，求 $k$ 的取值範圍',
        solution: {
          steps: [
            '$f(x) + k = x^2 - 6x + 8 + k = x^2 - 6x + (8+k)$',
            '要使此式恆為正，需判別式 $\\Delta < 0$',
            '$\\Delta = (-6)^2 - 4(1)(8+k) = 36 - 32 - 4k = 4 - 4k$',
            '由 $4 - 4k < 0$ 得 $k > 1$'
          ],
          answer: '$k > 1$'
        },
        hint: '利用判別式判斷二次函數的恆正條件'
      }
    }
  },
  {
    id: 'M08_01_03',
    topicId: 'M08_01',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '解含參數的二次不等式：',
    parts: {
      a: {
        question: '求解不等式 $x^2 - (m+1)x + m < 0$',
        solution: {
          steps: [
            '因式分解：$x^2 - (m+1)x + m = (x-1)(x-m)$',
            '不等式變為 $(x-1)(x-m) < 0$',
            '需要討論 $m$ 與 $1$ 的大小關係',
            '當 $m > 1$ 時，解為 $1 < x < m$；當 $m < 1$ 時，解為 $m < x < 1$；當 $m = 1$ 時，無解'
          ],
          answer: '當 $m > 1$ 時：$x \\in (1, m)$；當 $m < 1$ 時：$x \\in (m, 1)$；當 $m = 1$ 時：無解'
        },
        hint: '含參數的不等式需要分情況討論'
      },
      b: {
        question: '若不等式 $x^2 - 2mx + m^2 - 1 \\leq 0$ 有解，求 $m$ 的取值範圍',
        solution: {
          steps: [
            '配方：$x^2 - 2mx + m^2 - 1 = (x-m)^2 - 1$',
            '不等式變為 $(x-m)^2 \\leq 1$',
            '即 $|x-m| \\leq 1$，解為 $m-1 \\leq x \\leq m+1$',
            '此不等式對任意實數 $m$ 都有解'
          ],
          answer: '$m \\in \\mathbb{R}$'
        },
        hint: '配方後可以轉化為絕對值不等式'
      },
      c: {
        question: '求使不等式 $mx^2 - 2x + m > 0$ 恆成立的 $m$ 的取值範圍',
        solution: {
          steps: [
            '當 $m = 0$ 時，不等式變為 $-2x > 0$，即 $x < 0$，不恆成立',
            '當 $m \\neq 0$ 時，需要 $m > 0$ 且 $\\Delta < 0$',
            '判別式 $\\Delta = 4 - 4m^2 = 4(1-m^2)$',
            '由 $m > 0$ 且 $1-m^2 < 0$ 得 $m > 1$'
          ],
          answer: '$m > 1$'
        },
        hint: '分 $m=0$ 和 $m \\neq 0$ 兩種情況討論'
      }
    }
  },
  {
    id: 'M08_01_04',
    topicId: 'M08_01',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '應用二次不等式解決實際問題：',
    parts: {
      a: {
        question: '某物體從高度 $h_0 = 20$ 米處以初速度 $v_0 = 10$ 米/秒向上拋出，高度函數為 $h(t) = -5t^2 + 10t + 20$。求物體高度超過 $15$ 米的時間範圍',
        solution: {
          steps: [
            '建立不等式：$-5t^2 + 10t + 20 > 15$',
            '化簡：$-5t^2 + 10t + 5 > 0$，即 $t^2 - 2t - 1 < 0$',
            '使用公式法求根：$t = \\frac{2 \\pm \\sqrt{4+4}}{2} = 1 \\pm \\sqrt{2}$',
            '由於拋物線開口向上，解為 $1-\\sqrt{2} < t < 1+\\sqrt{2}$，約為 $-0.41 < t < 2.41$'
          ],
          answer: '約 $0$ 秒到 $2.41$ 秒（考慮 $t \\geq 0$）'
        },
        hint: '建立高度不等式，注意時間的實際意義'
      },
      b: {
        question: '求物體達到最高點的時間和最高高度',
        solution: {
          steps: [
            '對高度函數求導：$h\'(t) = -10t + 10$',
            '令 $h\'(t) = 0$：$-10t + 10 = 0$，得 $t = 1$ 秒',
            '最高高度：$h(1) = -5(1)^2 + 10(1) + 20 = 25$ 米',
            '驗證：$h\'\'(t) = -10 < 0$，確實為最高點'
          ],
          answer: '最高點時間：$1$ 秒，最高高度：$25$ 米'
        },
        hint: '利用導數求極值點'
      },
      c: {
        question: '求物體落地的時間',
        solution: {
          steps: [
            '令 $h(t) = 0$：$-5t^2 + 10t + 20 = 0$',
            '化簡：$t^2 - 2t - 4 = 0$',
            '使用公式法：$t = \\frac{2 \\pm \\sqrt{4+16}}{2} = \\frac{2 \\pm 2\\sqrt{5}}{2} = 1 \\pm \\sqrt{5}$',
            '取正值：$t = 1 + \\sqrt{5} \\approx 3.24$ 秒'
          ],
          answer: '$t = 1 + \\sqrt{5} \\approx 3.24$ 秒'
        },
        hint: '令高度為零，求解二次方程'
      }
    }
  },
  {
    id: 'M08_01_05',
    topicId: 'M08_01',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '綜合二次不等式問題：',
    parts: {
      a: {
        question: '已知不等式 $ax^2 + bx + c > 0$ 的解集為 $(-2, 3)$，求 $a$、$b$、$c$ 的關係',
        solution: {
          steps: [
            '由解集 $(-2, 3)$ 知，$ax^2 + bx + c = 0$ 的根為 $x = -2$ 和 $x = 3$',
            '因為解集為開區間，所以 $a < 0$（拋物線開口向下）',
            '根據韋達定理：$-2 + 3 = -\\frac{b}{a} = 1$，所以 $b = -a$',
            '$(-2) \\times 3 = \\frac{c}{a} = -6$，所以 $c = -6a$'
          ],
          answer: '$b = -a$，$c = -6a$，其中 $a < 0$'
        },
        hint: '根據解集的特點確定拋物線的開口方向和根'
      },
      b: {
        question: '若 $a = -1$，求不等式 $ax^2 + bx + c \\leq 0$ 的解集',
        solution: {
          steps: [
            '由上題得：$a = -1$，$b = 1$，$c = 6$',
            '不等式為 $-x^2 + x + 6 \\leq 0$，即 $x^2 - x - 6 \\geq 0$',
            '因式分解：$(x-3)(x+2) \\geq 0$',
            '解集為 $x \\leq -2$ 或 $x \\geq 3$'
          ],
          answer: '$x \\in (-\\infty, -2] \\cup [3, +\\infty)$'
        },
        hint: '注意不等號方向的變化'
      },
      c: {
        question: '求不等式 $|ax^2 + bx + c| < 1$ 的解集（其中 $a = -1$）',
        solution: {
          steps: [
            '不等式 $|{-x^2 + x + 6}| < 1$ 等價於 $-1 < -x^2 + x + 6 < 1$',
            '分解為兩個不等式：$-x^2 + x + 6 > -1$ 和 $-x^2 + x + 6 < 1$',
            '第一個：$x^2 - x - 7 < 0$，解為 $\\frac{1-\\sqrt{29}}{2} < x < \\frac{1+\\sqrt{29}}{2}$',
            '第二個：$x^2 - x - 5 > 0$，解為 $x < \\frac{1-\\sqrt{21}}{2}$ 或 $x > \\frac{1+\\sqrt{21}}{2}$'
          ],
          answer: '$x \\in (\\frac{1-\\sqrt{29}}{2}, \\frac{1-\\sqrt{21}}{2}) \\cup (\\frac{1+\\sqrt{21}}{2}, \\frac{1+\\sqrt{29}}{2})$'
        },
        hint: '絕對值不等式要分解為兩個普通不等式'
      }
    }
  },

  // M08-02: 二次方程求根
  {
    id: 'M08_02_01',
    topicId: 'M08_02',
    type: 'multi_part',
    difficulty: 'basic',
    question: '使用不同方法求解二次方程：',
    parts: {
      a: {
        question: '用因式分解法解方程 $x^2 - 7x + 12 = 0$',
        solution: {
          steps: [
            '尋找兩個數，使其乘積為 $12$，和為 $7$',
            '發現 $3 \\times 4 = 12$ 且 $3 + 4 = 7$',
            '因式分解：$x^2 - 7x + 12 = (x-3)(x-4) = 0$',
            '所以 $x = 3$ 或 $x = 4$'
          ],
          answer: '$x = 3$ 或 $x = 4$'
        },
        hint: '找出兩個數的乘積等於常數項，和等於一次項係數'
      },
      b: {
        question: '用配方法解方程 $x^2 - 6x + 5 = 0$',
        solution: {
          steps: [
            '移項：$x^2 - 6x = -5$',
            '配方：$x^2 - 6x + 9 = -5 + 9$',
            '得到：$(x-3)^2 = 4$',
            '開平方：$x - 3 = \\pm 2$，所以 $x = 3 \\pm 2$，即 $x = 1$ 或 $x = 5$'
          ],
          answer: '$x = 1$ 或 $x = 5$'
        },
        hint: '配方時加上一次項係數一半的平方'
      },
      c: {
        question: '用公式法解方程 $2x^2 + 3x - 2 = 0$',
        solution: {
          steps: [
            '識別係數：$a = 2$，$b = 3$，$c = -2$',
            '計算判別式：$\\Delta = b^2 - 4ac = 9 - 4(2)(-2) = 9 + 16 = 25$',
            '應用公式：$x = \\frac{-b \\pm \\sqrt{\\Delta}}{2a} = \\frac{-3 \\pm \\sqrt{25}}{4} = \\frac{-3 \\pm 5}{4}$',
            '得到：$x = \\frac{1}{2}$ 或 $x = -2$'
          ],
          answer: '$x = \\frac{1}{2}$ 或 $x = -2$'
        },
        hint: '使用二次方程求根公式 $x = \\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}$'
      }
    }
  },
  {
    id: 'M08_02_02',
    topicId: 'M08_02',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '解特殊形式的二次方程：',
    parts: {
      a: {
        question: '解方程 $(x-1)^2 = 16$',
        solution: {
          steps: [
            '直接開平方：$x - 1 = \\pm 4$',
            '解得：$x - 1 = 4$ 或 $x - 1 = -4$',
            '因此：$x = 5$ 或 $x = -3$',
            '驗證：$(5-1)^2 = 16$ ✓，$(-3-1)^2 = 16$ ✓'
          ],
          answer: '$x = 5$ 或 $x = -3$'
        },
        hint: '完全平方式可以直接開平方求解'
      },
      b: {
        question: '解方程 $x^2 - 4x = 0$',
        solution: {
          steps: [
            '提取公因數：$x(x - 4) = 0$',
            '由零乘積性質：$x = 0$ 或 $x - 4 = 0$',
            '解得：$x = 0$ 或 $x = 4$',
            '驗證：$0^2 - 4(0) = 0$ ✓，$4^2 - 4(4) = 0$ ✓'
          ],
          answer: '$x = 0$ 或 $x = 4$'
        },
        hint: '缺常數項的二次方程可以提取公因數'
      },
      c: {
        question: '解方程 $3x^2 - 12 = 0$',
        solution: {
          steps: [
            '移項：$3x^2 = 12$',
            '除以 $3$：$x^2 = 4$',
            '開平方：$x = \\pm 2$',
            '驗證：$3(2)^2 - 12 = 0$ ✓，$3(-2)^2 - 12 = 0$ ✓'
          ],
          answer: '$x = 2$ 或 $x = -2$'
        },
        hint: '缺一次項的二次方程可以直接移項開平方'
      }
    }
  },
  {
    id: 'M08_02_03',
    topicId: 'M08_02',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '解含分數的二次方程：',
    parts: {
      a: {
        question: '解方程 $\\frac{x^2}{4} - \\frac{x}{2} - 3 = 0$',
        solution: {
          steps: [
            '兩邊乘以 $4$ 消除分母：$x^2 - 2x - 12 = 0$',
            '因式分解：尋找兩數乘積為 $-12$，和為 $-2$',
            '發現 $(-6) \\times 2 = -12$ 且 $-6 + 2 = -4$，不對',
            '使用公式法：$x = \\frac{2 \\pm \\sqrt{4 + 48}}{2} = \\frac{2 \\pm \\sqrt{52}}{2} = \\frac{2 \\pm 2\\sqrt{13}}{2} = 1 \\pm \\sqrt{13}$'
          ],
          answer: '$x = 1 + \\sqrt{13}$ 或 $x = 1 - \\sqrt{13}$'
        },
        hint: '先消除分母，再選擇適當的求解方法'
      },
      b: {
        question: '解方程 $\\frac{2x}{x-1} = \\frac{3}{x+1}$',
        solution: {
          steps: [
            '交叉相乘：$2x(x+1) = 3(x-1)$',
            '展開：$2x^2 + 2x = 3x - 3$',
            '整理：$2x^2 - x + 3 = 0$',
            '判別式：$\\Delta = 1 - 4(2)(3) = 1 - 24 = -23 < 0$，無實數解'
          ],
          answer: '無實數解'
        },
        hint: '分式方程要注意定義域的限制'
      },
      c: {
        question: '解方程 $x + \\frac{1}{x} = \\frac{5}{2}$',
        solution: {
          steps: [
            '兩邊乘以 $x$（$x \\neq 0$）：$x^2 + 1 = \\frac{5x}{2}$',
            '整理：$2x^2 + 2 = 5x$，即 $2x^2 - 5x + 2 = 0$',
            '因式分解：$(2x-1)(x-2) = 0$',
            '解得：$x = \\frac{1}{2}$ 或 $x = 2$'
          ],
          answer: '$x = \\frac{1}{2}$ 或 $x = 2$'
        },
        hint: '注意 $x \\neq 0$ 的限制條件'
      }
    }
  },
  {
    id: 'M08_02_04',
    topicId: 'M08_02',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '解複雜的二次方程：',
    parts: {
      a: {
        question: '解方程 $(x+1)(x-3) = (x+2)(x-4)$',
        solution: {
          steps: [
            '展開左邊：$(x+1)(x-3) = x^2 - 2x - 3$',
            '展開右邊：$(x+2)(x-4) = x^2 - 2x - 8$',
            '建立方程：$x^2 - 2x - 3 = x^2 - 2x - 8$',
            '化簡：$-3 = -8$，這是矛盾，所以原方程無解'
          ],
          answer: '無解'
        },
        hint: '展開後化簡，注意是否出現矛盾'
      },
      b: {
        question: '解方程 $\\sqrt{x^2 - 3x + 2} = x - 1$',
        solution: {
          steps: [
            '定義域：$x^2 - 3x + 2 \\geq 0$ 且 $x - 1 \\geq 0$',
            '因式分解：$(x-1)(x-2) \\geq 0$，所以 $x \\leq 1$ 或 $x \\geq 2$',
            '結合 $x \\geq 1$，得 $x = 1$ 或 $x \\geq 2$',
            '兩邊平方：$x^2 - 3x + 2 = (x-1)^2 = x^2 - 2x + 1$，得 $x = 1$'
          ],
          answer: '$x = 1$'
        },
        hint: '根式方程要先確定定義域，然後兩邊平方'
      },
      c: {
        question: '解方程 $x^4 - 5x^2 + 4 = 0$',
        solution: {
          steps: [
            '設 $y = x^2$，原方程變為 $y^2 - 5y + 4 = 0$',
            '因式分解：$(y-1)(y-4) = 0$',
            '得 $y = 1$ 或 $y = 4$',
            '回代：$x^2 = 1$ 或 $x^2 = 4$，所以 $x = \\pm 1$ 或 $x = \\pm 2$'
          ],
          answer: '$x = \\pm 1$ 或 $x = \\pm 2$'
        },
        hint: '四次方程可以用換元法轉化為二次方程'
      }
    }
  },
  {
    id: 'M08_02_05',
    topicId: 'M08_02',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '應用題中的二次方程：',
    parts: {
      a: {
        question: '一個矩形的長比寬多 $3$ 米，面積是 $40$ 平方米，求矩形的長和寬',
        solution: {
          steps: [
            '設寬為 $x$ 米，則長為 $(x+3)$ 米',
            '根據面積條件：$x(x+3) = 40$',
            '展開：$x^2 + 3x = 40$，即 $x^2 + 3x - 40 = 0$',
            '因式分解：$(x+8)(x-5) = 0$，得 $x = 5$（負值不合實際）'
          ],
          answer: '寬 $5$ 米，長 $8$ 米'
        },
        hint: '設未知數，根據幾何條件建立方程'
      },
      b: {
        question: '某商品原價 $100$ 元，連續兩次降價後價格為 $81$ 元，求每次降價的百分率（兩次降價百分率相同）',
        solution: {
          steps: [
            '設每次降價百分率為 $x$',
            '第一次降價後：$100(1-x)$ 元',
            '第二次降價後：$100(1-x)^2 = 81$ 元',
            '解方程：$(1-x)^2 = 0.81$，得 $1-x = 0.9$，所以 $x = 0.1 = 10\\%$'
          ],
          answer: '每次降價 $10\\%$'
        },
        hint: '連續百分率變化用乘法原理'
      },
      c: {
        question: '一球從 $45$ 米高處自由落下，何時距地面 $20$ 米？（重力加速度 $g = 10$ 米/秒²）',
        solution: {
          steps: [
            '自由落體位移公式：$h = h_0 - \\frac{1}{2}gt^2 = 45 - 5t^2$',
            '當距地面 $20$ 米時：$45 - 5t^2 = 20$',
            '解方程：$5t^2 = 25$，得 $t^2 = 5$',
            '所以 $t = \\sqrt{5} \\approx 2.24$ 秒'
          ],
          answer: '$t = \\sqrt{5} \\approx 2.24$ 秒'
        },
        hint: '利用物理公式建立二次方程'
      }
    }
  },

  // M08-03: 二次方程求解 - 添加5個問題
  {
    id: 'M08_03_01',
    topicId: 'M08_03',
    type: 'multi_part',
    difficulty: 'basic',
    question: '選擇合適的方法求解二次方程：',
    parts: {
      a: {
        question: '解方程 $x^2 - 9 = 0$',
        solution: {
          steps: [
            '移項：$x^2 = 9$',
            '直接開平方：$x = \\pm 3$',
            '驗證：$3^2 - 9 = 0$ ✓，$(-3)^2 - 9 = 0$ ✓',
            '這是最簡單的求解方法'
          ],
          answer: '$x = 3$ 或 $x = -3$'
        },
        hint: '對於 $x^2 = k$ 的形式，直接開平方最簡單'
      },
      b: {
        question: '解方程 $x^2 + 6x + 9 = 0$',
        solution: {
          steps: [
            '識別完全平方：$x^2 + 6x + 9 = (x+3)^2$',
            '方程變為 $(x+3)^2 = 0$',
            '開平方：$x + 3 = 0$',
            '解得：$x = -3$（重根）'
          ],
          answer: '$x = -3$（重根）'
        },
        hint: '完全平方三項式可以直接因式分解'
      },
      c: {
        question: '解方程 $2x^2 - 7x + 3 = 0$',
        solution: {
          steps: [
            '嘗試因式分解：尋找 $(2x + a)(x + b) = 2x^2 + (2b+a)x + ab$',
            '需要 $ab = 3$，$2b + a = -7$',
            '嘗試 $a = -1$，$b = -3$：$(2x-1)(x-3) = 2x^2 - 7x + 3$ ✓',
            '解得：$x = \\frac{1}{2}$ 或 $x = 3$'
          ],
          answer: '$x = \\frac{1}{2}$ 或 $x = 3$'
        },
        hint: '對於二次項係數不為1的方程，嘗試十字相乘法'
      }
    }
  },
  {
    id: 'M08_03_02',
    topicId: 'M08_03',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '解需要技巧的二次方程：',
    parts: {
      a: {
        question: '解方程 $x^2 - 2\\sqrt{3}x + 3 = 0$',
        solution: {
          steps: [
            '使用公式法：$a = 1$，$b = -2\\sqrt{3}$，$c = 3$',
            '判別式：$\\Delta = (-2\\sqrt{3})^2 - 4(1)(3) = 12 - 12 = 0$',
            '由於 $\\Delta = 0$，方程有重根',
            '$x = \\frac{2\\sqrt{3}}{2} = \\sqrt{3}$'
          ],
          answer: '$x = \\sqrt{3}$（重根）'
        },
        hint: '當判別式為零時，方程有重根'
      },
      b: {
        question: '解方程 $(x-1)(x+2) = (x+3)(x-4)$',
        solution: {
          steps: [
            '展開左邊：$(x-1)(x+2) = x^2 + x - 2$',
            '展開右邊：$(x+3)(x-4) = x^2 - x - 12$',
            '建立方程：$x^2 + x - 2 = x^2 - x - 12$',
            '化簡：$2x = -10$，所以 $x = -5$'
          ],
          answer: '$x = -5$'
        },
        hint: '展開後可能會約去二次項，變成一次方程'
      },
      c: {
        question: '解方程 $\\frac{x}{x-2} + \\frac{x-2}{x} = \\frac{5}{2}$',
        solution: {
          steps: [
            '定義域：$x \\neq 0$ 且 $x \\neq 2$',
            '通分：$\\frac{x^2 + (x-2)^2}{x(x-2)} = \\frac{5}{2}$',
            '化簡分子：$x^2 + x^2 - 4x + 4 = 2x^2 - 4x + 4$',
            '交叉相乘：$2(2x^2 - 4x + 4) = 5x(x-2)$，得 $4x^2 - 8x + 8 = 5x^2 - 10x$',
            '整理：$x^2 - 2x - 8 = 0$，因式分解：$(x-4)(x+2) = 0$',
            '解得：$x = 4$ 或 $x = -2$（都滿足定義域）'
          ],
          answer: '$x = 4$ 或 $x = -2$'
        },
        hint: '分式方程要先確定定義域，再通分化簡'
      }
    }
  },
  {
    id: 'M08_03_03',
    topicId: 'M08_03',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '解含絕對值的二次方程：',
    parts: {
      a: {
        question: '解方程 $|x^2 - 4| = 5$',
        solution: {
          steps: [
            '絕對值方程分兩種情況：$x^2 - 4 = 5$ 或 $x^2 - 4 = -5$',
            '情況1：$x^2 = 9$，得 $x = \\pm 3$',
            '情況2：$x^2 = -1$，無實數解',
            '驗證：$|9-4| = 5$ ✓，$|9-4| = 5$ ✓'
          ],
          answer: '$x = 3$ 或 $x = -3$'
        },
        hint: '絕對值方程要分正負兩種情況討論'
      },
      b: {
        question: '解方程 $|x+1| = x^2 - 1$',
        solution: {
          steps: [
            '需要 $x^2 - 1 \\geq 0$，即 $x \\leq -1$ 或 $x \\geq 1$',
            '當 $x \\geq -1$ 時：$x + 1 = x^2 - 1$，得 $x^2 - x - 2 = 0$',
            '因式分解：$(x-2)(x+1) = 0$，得 $x = 2$ 或 $x = -1$',
            '當 $x \\leq -1$ 時：$-(x+1) = x^2 - 1$，得 $x^2 + x = 0$',
            '$x(x+1) = 0$，得 $x = 0$ 或 $x = -1$，但 $x = 0$ 不滿足 $x \\leq -1$',
            '綜合：$x = 2$，$x = -1$'
          ],
          answer: '$x = 2$ 或 $x = -1$'
        },
        hint: '要先確定右邊非負，再分情況討論絕對值'
      },
      c: {
        question: '解方程 $x^2 - 2|x| - 3 = 0$',
        solution: {
          steps: [
            '設 $t = |x| \\geq 0$，方程變為 $t^2 - 2t - 3 = 0$',
            '因式分解：$(t-3)(t+1) = 0$',
            '得 $t = 3$ 或 $t = -1$，由於 $t \\geq 0$，所以 $t = 3$',
            '即 $|x| = 3$，所以 $x = 3$ 或 $x = -3$'
          ],
          answer: '$x = 3$ 或 $x = -3$'
        },
        hint: '用換元法，設 $t = |x|$ 來化簡方程'
      }
    }
  },
  {
    id: 'M08_03_04',
    topicId: 'M08_03',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '解參數方程：',
    parts: {
      a: {
        question: '已知關於 $x$ 的方程 $x^2 - (k+1)x + k = 0$，求使方程有兩個相等實根的 $k$ 值',
        solution: {
          steps: [
            '方程有兩個相等實根當且僅當判別式 $\\Delta = 0$',
            '$\\Delta = (k+1)^2 - 4(1)(k) = k^2 + 2k + 1 - 4k = k^2 - 2k + 1$',
            '令 $\\Delta = 0$：$k^2 - 2k + 1 = 0$',
            '因式分解：$(k-1)^2 = 0$，得 $k = 1$'
          ],
          answer: '$k = 1$'
        },
        hint: '重根條件是判別式等於零'
      },
      b: {
        question: '當 $k = 1$ 時，求方程的根',
        solution: {
          steps: [
            '代入 $k = 1$：$x^2 - 2x + 1 = 0$',
            '因式分解：$(x-1)^2 = 0$',
            '得到重根：$x = 1$',
            '驗證：$1^2 - 2(1) + 1 = 0$ ✓'
          ],
          answer: '$x = 1$（重根）'
        },
        hint: '代入參數值求解具體方程'
      },
      c: {
        question: '若方程 $x^2 - (k+1)x + k = 0$ 的兩根之積為 $6$，求 $k$ 的值',
        solution: {
          steps: [
            '根據韋達定理，兩根之積等於 $\\frac{c}{a} = \\frac{k}{1} = k$',
            '由題意：$k = 6$',
            '驗證：當 $k = 6$ 時，方程為 $x^2 - 7x + 6 = 0$',
            '因式分解：$(x-1)(x-6) = 0$，兩根為 $x = 1$ 和 $x = 6$，積為 $6$ ✓'
          ],
          answer: '$k = 6$'
        },
        hint: '利用韋達定理：兩根之積等於 $\\frac{c}{a}$'
      }
    }
  },
  {
    id: 'M08_03_05',
    topicId: 'M08_03',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '綜合求解問題：',
    parts: {
      a: {
        question: '解方程組：$\\begin{cases} x + y = 5 \\\\ xy = 6 \\end{cases}$',
        solution: {
          steps: [
            '由第一個方程得：$y = 5 - x$',
            '代入第二個方程：$x(5-x) = 6$',
            '展開：$5x - x^2 = 6$，即 $x^2 - 5x + 6 = 0$',
            '因式分解：$(x-2)(x-3) = 0$，得 $x = 2$ 或 $x = 3$',
            '對應的 $y$ 值：當 $x = 2$ 時 $y = 3$；當 $x = 3$ 時 $y = 2$'
          ],
          answer: '$(x,y) = (2,3)$ 或 $(3,2)$'
        },
        hint: '用代入法消元，轉化為二次方程'
      },
      b: {
        question: '求兩個數，使它們的和為 $10$，平方和為 $58$',
        solution: {
          steps: [
            '設兩數為 $x$ 和 $y$，建立方程組：$\\begin{cases} x + y = 10 \\\\ x^2 + y^2 = 58 \\end{cases}$',
            '由第一個方程：$y = 10 - x$',
            '代入第二個方程：$x^2 + (10-x)^2 = 58$',
            '展開：$x^2 + 100 - 20x + x^2 = 58$，即 $2x^2 - 20x + 42 = 0$',
            '化簡：$x^2 - 10x + 21 = 0$，因式分解：$(x-3)(x-7) = 0$',
            '得 $x = 3$ 或 $x = 7$，對應 $y = 7$ 或 $y = 3$'
          ],
          answer: '兩數為 $3$ 和 $7$'
        },
        hint: '利用 $(x+y)^2 = x^2 + 2xy + y^2$ 的關係'
      },
      c: {
        question: '解方程 $\\sqrt{x+3} + \\sqrt{x-1} = 4$',
        solution: {
          steps: [
            '定義域：$x \\geq 1$',
            '設 $\\sqrt{x+3} = u$，$\\sqrt{x-1} = v$，則 $u + v = 4$',
            '且 $u^2 - v^2 = (x+3) - (x-1) = 4$，即 $(u+v)(u-v) = 4$',
            '由 $u + v = 4$ 得 $u - v = 1$',
            '解得：$u = \\frac{5}{2}$，$v = \\frac{3}{2}$',
            '即 $\\sqrt{x+3} = \\frac{5}{2}$，得 $x + 3 = \\frac{25}{4}$，所以 $x = \\frac{13}{4}$'
          ],
          answer: '$x = \\frac{13}{4}$'
        },
        hint: '用換元法設兩個根式為新變量'
      }
    }
  }
];

// 導出模組
export default {
  topics: M08_TOPICS_01_TO_10,
  problems: M08_PROBLEMS_01_TO_10
}; 